import * as React from "react";
import { useEffect, useId, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Smile, X } from "@thuthi/ui/icons";
import { Badge } from "@thuthi/ui/components/badge";
import { Button } from "@thuthi/ui/components/button";
import { Checkbox } from "@thuthi/ui/components/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@thuthi/ui/components/dialog";
import { Input } from "@thuthi/ui/components/input";
import { Label } from "@thuthi/ui/components/label";
import { Textarea } from "@thuthi/ui/components/textarea";
import { formatBytes, validTopic } from "../app/utils";
import AttachmentIcon from "./AttachmentIcon";
import DialogFooter from "./DialogFooter";
import api, { type PublishRequest, type V2Config } from "../app/Api";
import EmojiPicker from "./EmojiPicker";
import session from "../app/Session";
import routes from "./routes";
import { UnauthorizedError } from "../app/errors";

/**
 * 메시지 보내기 — POST /api/v2/messages 하나로 보낸다(apps/server/ntfy/PLAN.md). 파일이 있으면 multipart 로
 * 글과 파일 여러 개를 한 번에, 없으면 JSON. 앱 웹뷰 안에서도 같은 길이다(예전 네이티브 브리지 publish 는 쓰지 않는다).
 * 파일은 고르기·끌어다 놓기·붙여 넣기로 여러 개 더하고, 보내기 전에 하나씩 뺄 수 있다. 한도는 GET /api/v2/config.
 */

interface PublishDialogProps {
  /** OPEN_MODE_DEFAULT | OPEN_MODE_DRAG | "" (닫힘) */
  openMode: string;
  topic: string;
  message: string;
  attachFiles?: File[];
  getPastedImage: (ev: React.ClipboardEvent) => File[];
  onClose: () => void;
  onCancel?: () => void;
  onDragEnter: () => void;
  onResetOpenMode: () => void;
}

const PublishDialog = (props: PublishDialogProps) => {
  const { t } = useTranslation();
  const [topic, setTopic] = useState("");
  const [message, setMessage] = useState("");
  const [messageFocused, setMessageFocused] = useState(true);
  const [title, setTitle] = useState("");
  const [tags, setTags] = useState("");
  const [files, setFiles] = useState<File[]>([]);
  const [publishAnother, setPublishAnother] = useState(false);
  const [limits, setLimits] = useState<V2Config | null>(null);

  const [showTopic, setShowTopic] = useState(false);

  const attachFileInput = useRef<HTMLInputElement>(null);
  const [activeRequest, setActiveRequest] = useState<PublishRequest | null>(null);
  const [status, setStatus] = useState<React.ReactNode>("");
  const disabled = !!activeRequest;

  const [emojiPickerAnchorEl, setEmojiPickerAnchorEl] = useState<HTMLElement | null>(null);
  const [dropZone, setDropZone] = useState(false);

  const open = !!props.openMode;

  useEffect(() => {
    window.addEventListener("dragenter", () => {
      props.onDragEnter();
      setDropZone(true);
    });
  }, []);

  useEffect(() => {
    setTopic(props.topic);
    setShowTopic(!props.topic);
    setMessageFocused(!!props.topic); // Focus message only if topic is set
  }, [props.topic]);

  useEffect(() => {
    setMessage(props.message);
  }, [props.message]);

  useEffect(() => {
    if (!open || limits) {
      return;
    }
    api
      .config()
      .then(setLimits)
      .catch(async (e) => {
        console.log(`[PublishDialog] Retrieving attachment limits failed`, e);
        if (e instanceof UnauthorizedError) {
          await session.resetAndRedirect(routes.login);
        }
      });
  }, [open, limits]);

  // 한도: 파일 하나 크기·메시지당 개수(서버도 다시 확인한다)
  const fileError = useMemo(() => {
    if (!limits) {
      return "";
    }
    if (files.length > limits.attachments_per_message) {
      return `파일은 한 번에 ${limits.attachments_per_message}개까지 보낼 수 있다`;
    }
    const tooBig = files.find((f) => f.size > limits.attachment_file_limit);
    if (tooBig) {
      return t("publish_dialog_attachment_limits_file_reached", { fileSizeLimit: formatBytes(limits.attachment_file_limit) }) + ` (${tooBig.name})`;
    }
    return "";
  }, [files, limits, t]);

  // v2 는 제목·본문이 둘 다 필수다(서버 v2.build_message) — 사진만 보낼 때도 제목·본문을 적는다
  const missingText = !title.trim() || !message.trim();
  const sendButtonEnabled = !!validTopic(topic) && !fileError && !missingText;

  const addFiles = (incoming: File[] | FileList | null | undefined) => {
    const list = Array.from(incoming ?? []).filter((f) => f.size > 0 || f.type);
    if (list.length === 0) {
      return;
    }
    setFiles((prev) => [...prev, ...list]);
    props.onResetOpenMode();
  };

  useEffect(() => {
    if (props.attachFiles && props.attachFiles.length > 0) {
      addFiles(props.attachFiles);
    }
  }, [props.attachFiles]);

  const handleSubmit = async () => {
    const progressFn = (ev: ProgressEvent) => {
      if (ev.loaded > 0 && ev.total > 0) {
        setStatus(
          t("publish_dialog_progress_uploading_detail", {
            loaded: formatBytes(ev.loaded),
            total: formatBytes(ev.total),
            percent: Math.round((ev.loaded * 100.0) / ev.total),
          })
        );
      } else {
        setStatus(t("publish_dialog_progress_uploading"));
      }
    };
    try {
      const request = api.publish(
        {
          topic,
          title: title.trim(),
          message: message.trim(),
          tags: tags.trim(),
        },
        files,
        files.length > 0 ? progressFn : undefined
      );
      setActiveRequest(request);
      await request;
      if (!publishAnother) {
        props.onClose();
      } else {
        setStatus(t("publish_dialog_message_published"));
        setFiles([]);
        setActiveRequest(null);
      }
    } catch (e) {
      setStatus(<span className="text-destructive block max-w-[400px]">{(e as Error).message}</span>);
      setActiveRequest(null);
    }
  };

  const handlePaste = (ev: React.ClipboardEvent) => {
    addFiles(props.getPastedImage(ev));
  };

  const handleAttachFileDrop = async (ev: React.DragEvent) => {
    ev.preventDefault();
    setDropZone(false);
    addFiles(ev.dataTransfer.files);
  };

  const handleAttachFileDragLeave = () => {
    setDropZone(false);
    if (props.openMode === PublishDialog.OPEN_MODE_DRAG) {
      props.onClose(); // Only close dialog if it was not open before dragging file in
    }
  };

  const handleEmojiPick = (emoji: string) => {
    setTags((prevTags) => (prevTags.trim() ? `${prevTags.trim()}, ${emoji}` : emoji));
  };

  return (
    <>
      {dropZone && <DropArea onDrop={handleAttachFileDrop} onDragLeave={handleAttachFileDragLeave} />}
      <Dialog
        open={open}
        onOpenChange={(isOpen) => {
          // Messaging 은 onCancel 을 넘기지 않으므로 Escape/바깥 클릭으로는 닫히지 않는 것이 원본 동작이다.
          if (!isOpen) {
            props.onCancel?.();
          }
        }}
      >
        <DialogContent
          showCloseButton={false}
          aria-describedby={undefined}
          onOpenAutoFocus={(ev) => ev.preventDefault()} // React 의 autoFocus(topic/message 필드)가 초점을 갖도록
          className="flex h-dvh max-h-dvh w-full max-w-none flex-col gap-0 overflow-hidden rounded-none border-0 p-0 sm:h-auto sm:max-h-[calc(100dvh-4rem)] sm:max-w-[min(900px,calc(100%-4rem))] sm:rounded-lg sm:border"
        >
          {dropZone && <DropBox />}
          <DialogHeader className="shrink-0 px-6 pt-6 text-left">
            <DialogTitle>{topic ? t("publish_dialog_title_topic", { topic }) : t("publish_dialog_title_no_topic")}</DialogTitle>
          </DialogHeader>
          <div className="min-h-0 grow overflow-y-auto px-6 pt-1 pb-2">
            {showTopic && (
              <ClosableRow
                closable={!!props.topic}
                disabled={disabled}
                closeLabel={t("publish_dialog_topic_reset")}
                onClose={() => {
                  setTopic(props.topic);
                  setShowTopic(false);
                }}
              >
                <Field
                  label={t("publish_dialog_topic_label")}
                  placeholder={t("publish_dialog_topic_placeholder")}
                  value={topic}
                  onChange={(ev) => setTopic(ev.target.value)}
                  disabled={disabled}
                  type="text"
                  autoFocus={!messageFocused}
                  className="grow max-sm:min-w-full"
                  aria-label={t("publish_dialog_topic_label")}
                />
              </ClosableRow>
            )}
            <Field
              label={t("publish_dialog_title_label")}
              placeholder={t("publish_dialog_title_placeholder")}
              value={title}
              onChange={(ev) => setTitle(ev.target.value)}
              disabled={disabled}
              type="text"
              className="w-full"
              aria-label={t("publish_dialog_title_label")}
            />
            <Field
              label={t("publish_dialog_message_label")}
              placeholder={t("publish_dialog_message_placeholder")}
              value={message}
              onChange={(ev) => setMessage(ev.target.value)}
              disabled={disabled}
              multiline
              rows={5}
              autoFocus={messageFocused}
              className="w-full"
              inputClassName="min-h-[7.5rem]"
              aria-label={t("publish_dialog_message_label")}
              onPaste={handlePaste}
            />
            {missingText && <p className="text-muted-foreground mt-1.5 text-xs">제목과 내용을 모두 적어야 보낼 수 있다</p>}
            <div className="flex flex-wrap items-end gap-x-2">
              <EmojiPicker anchorEl={emojiPickerAnchorEl} onEmojiPick={handleEmojiPick} onClose={() => setEmojiPickerAnchorEl(null)} />
              <DialogIconButton
                disabled={disabled}
                onClick={(ev) => setEmojiPickerAnchorEl(ev.currentTarget)}
                aria-label={t("publish_dialog_emoji_picker_show")}
              >
                <Smile />
              </DialogIconButton>
              <Field
                label={t("publish_dialog_tags_label")}
                placeholder={t("publish_dialog_tags_placeholder")}
                value={tags}
                onChange={(ev) => setTags(ev.target.value)}
                disabled={disabled}
                type="text"
                className="flex-[1_1_180px] max-sm:min-w-[calc(100%-44px)] sm:min-w-[180px]"
                aria-label={t("publish_dialog_tags_label")}
              />
            </div>
            <input
              type="file"
              multiple
              ref={attachFileInput}
              onChange={(ev) => {
                addFiles(ev.target.files);
                ev.target.value = ""; // 같은 파일을 다시 고를 수 있게
              }}
              className="hidden"
              aria-hidden
            />
            {files.length > 0 && (
              <AttachmentList
                files={files}
                disabled={disabled}
                error={fileError}
                onAdd={() => attachFileInput.current!.click()}
                onRemove={(index) => setFiles((prev) => prev.filter((_, i) => i !== index))}
              />
            )}
            <p className="mt-4 mb-2 text-sm">{t("publish_dialog_other_features")}</p>
            <div>
              {files.length === 0 && (
                <FeatureChip
                  disabled={disabled}
                  label="사진·파일 첨부"
                  aria-label="사진·파일 첨부"
                  onClick={() => attachFileInput.current!.click()}
                />
              )}
              {!showTopic && (
                <FeatureChip
                  disabled={disabled}
                  label={t("publish_dialog_chip_topic_label")}
                  aria-label={t("publish_dialog_chip_topic_label")}
                  onClick={() => setShowTopic(true)}
                />
              )}
            </div>
          </div>
          <div className="shrink-0 px-6 pb-4">
            <DialogFooter status={status}>
              {activeRequest && (
                <Button variant="ghost" onClick={() => activeRequest.abort()}>
                  {t("publish_dialog_button_cancel_sending")}
                </Button>
              )}
              {!activeRequest && (
                <>
                  <label className="flex items-center gap-2 text-sm max-sm:basis-full sm:mr-4">
                    <Checkbox
                      checked={publishAnother}
                      onCheckedChange={(checked) => setPublishAnother(checked === true)}
                      aria-label={t("publish_dialog_checkbox_publish_another")}
                    />
                    {t("publish_dialog_checkbox_publish_another")}
                  </label>
                  <Button variant="ghost" onClick={props.onClose}>
                    {t("publish_dialog_button_cancel")}
                  </Button>
                  <Button onClick={handleSubmit} disabled={!sendButtonEnabled}>
                    {t("publish_dialog_button_send")}
                  </Button>
                </>
              )}
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

interface RowProps {
  children: React.ReactNode;
}

const Row = (props: RowProps) => (
  <div role="row" className="flex flex-wrap items-end gap-x-2 sm:flex-nowrap">
    {props.children}
  </div>
);

interface ClosableRowProps {
  closable?: boolean;
  disabled?: boolean;
  closeLabel: string;
  onClose: () => void;
  children: React.ReactNode;
}

const ClosableRow = (props: ClosableRowProps) => {
  const closable = props.closable !== undefined ? props.closable : true;
  return (
    <Row>
      {props.children}
      {closable && (
        <DialogIconButton disabled={props.disabled} onClick={props.onClose} className="ml-1.5" aria-label={props.closeLabel}>
          <X />
        </DialogIconButton>
      )}
    </Row>
  );
};

interface DialogIconButtonProps {
  className?: string;
  disabled?: boolean;
  onClick?: (ev: React.MouseEvent<HTMLButtonElement>) => void;
  "aria-label"?: string;
  children: React.ReactNode;
}

const DialogIconButton = (props: DialogIconButtonProps) => (
  <Button
    type="button"
    variant="ghost"
    size="icon"
    className={`text-muted-foreground shrink-0 ${props.className || ""}`}
    onClick={props.onClick}
    disabled={props.disabled}
    aria-label={props["aria-label"]}
  >
    {props.children}
  </Button>
);

/**
 * MUI TextField(variant="standard") 대응: 상단 라벨 + Input/Textarea.
 * className 은 래퍼(폭/플렉스 제어), inputClassName 은 입력 요소에 적용된다.
 */
interface FieldProps {
  label: React.ReactNode;
  className?: string;
  inputClassName?: string;
  multiline?: boolean;
  rows?: number;
  type?: string;
  placeholder?: string;
  value: string;
  disabled?: boolean;
  autoFocus?: boolean;
  "aria-label"?: string;
  onChange: (ev: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  onPaste?: (ev: React.ClipboardEvent) => void;
}

const Field = ({ label, className = "", inputClassName, multiline, ...inputProps }: FieldProps) => {
  const id = useId();
  const InputComponent: React.ElementType = multiline ? Textarea : Input;
  return (
    <div className={`flex flex-col gap-1.5 pt-2 ${className}`}>
      <Label htmlFor={id} className="text-muted-foreground">
        {label}
      </Label>
      <InputComponent id={id} className={inputClassName} {...inputProps} />
    </div>
  );
};

interface FeatureChipProps {
  label: React.ReactNode;
  disabled?: boolean;
  onClick?: () => void;
  "aria-label"?: string;
}

// MUI Chip(clickable) 대응
const FeatureChip = (props: FeatureChipProps) => (
  <Badge
    asChild
    variant="secondary"
    className="hover:bg-secondary/80 mr-2 mb-2 cursor-pointer rounded-full px-3 py-1 disabled:pointer-events-none disabled:opacity-50"
  >
    <button type="button" disabled={props.disabled} onClick={props.onClick} aria-label={props["aria-label"]}>
      {props.label}
    </button>
  </Badge>
);

interface AttachmentListProps {
  files: File[];
  disabled?: boolean;
  error?: string;
  onAdd: () => void;
  onRemove: (index: number) => void;
}

/** 보낼 파일 목록 — 이미지는 미리 보기, 나머지는 아이콘. 칸마다 빼기 버튼, 끝에 더하기 칸 */
const AttachmentList = (props: AttachmentListProps) => {
  const { t } = useTranslation();
  const previews = useMemo(() => props.files.map((f) => (f.type.startsWith("image/") ? URL.createObjectURL(f) : null)), [props.files]);
  useEffect(() => () => previews.forEach((url) => url && URL.revokeObjectURL(url)), [previews]);
  const total = props.files.reduce((sum, f) => sum + f.size, 0);
  return (
    <div className="mt-4">
      <p className="text-sm">
        {t("publish_dialog_attached_file_title")} {props.files.length}개 · {formatBytes(total)}
        {props.error && (
          <span className="text-destructive block" aria-live="polite">
            {props.error}
          </span>
        )}
      </p>
      <div className="mt-2 grid grid-cols-3 gap-2 sm:grid-cols-5">
        {props.files.map((file, index) => (
          <div key={`${file.name}-${index}`} className="bg-muted relative aspect-square overflow-hidden rounded-lg border">
            {previews[index] ? (
              <img src={previews[index]!} alt={file.name} className="size-full object-cover" />
            ) : (
              <div className="flex size-full flex-col items-center justify-center gap-1 p-1.5 text-center">
                <AttachmentIcon type={file.type} />
                <span className="line-clamp-2 text-[11px] break-all">{file.name}</span>
              </div>
            )}
            <span className="absolute right-0 bottom-0 left-0 truncate bg-black/45 px-1.5 py-0.5 text-[10px] text-white">{formatBytes(file.size, 1)}</span>
            <Button
              type="button"
              variant="secondary"
              size="icon"
              disabled={props.disabled}
              onClick={() => props.onRemove(index)}
              aria-label={`${t("publish_dialog_attached_file_remove")}: ${file.name}`}
              className="absolute top-1 right-1 size-6 rounded-full bg-black/55 text-white hover:bg-black/70"
            >
              <X className="size-3.5" />
            </Button>
          </div>
        ))}
        <button
          type="button"
          disabled={props.disabled}
          onClick={props.onAdd}
          className="text-muted-foreground hover:border-primary hover:text-primary flex aspect-square items-center justify-center rounded-lg border-2 border-dashed text-sm disabled:opacity-50"
        >
          + 더하기
        </button>
      </div>
    </div>
  );
};

interface DropAreaProps {
  onDrop: (ev: React.DragEvent) => void;
  onDragLeave: () => void;
}

const DropArea = (props: DropAreaProps) => {
  const allowDrag = (ev: React.DragEvent) => {
    // This is where we could disallow certain files to be dragged in.
    // For now we allow all files.

    // eslint-disable-next-line no-param-reassign
    ev.dataTransfer.dropEffect = "copy";
    ev.preventDefault();
  };

  return (
    // Radix 모달 다이얼로그가 body 에 pointer-events: none 을 걸기 때문에,
    // 다이얼로그 밖에 렌더링되는 이 드롭 영역은 pointer-events-auto 를 명시해야 한다.
    <div
      className="pointer-events-auto fixed inset-0 z-[10002]"
      onDrop={props.onDrop}
      onDragEnter={allowDrag}
      onDragOver={allowDrag}
      onDragLeave={props.onDragLeave}
    />
  );
};

const DropBox = () => {
  const { t } = useTranslation();
  return (
    <div className="bg-background/75 absolute inset-0 z-[10000] rounded-[inherit]">
      <div className="border-muted-foreground/40 absolute inset-10 z-[10001] flex items-center justify-center rounded-[5px] border-[3px] border-dashed">
        <p className="text-2xl">{t("publish_dialog_drop_file_here")}</p>
      </div>
    </div>
  );
};

PublishDialog.OPEN_MODE_DEFAULT = "default";
PublishDialog.OPEN_MODE_DRAG = "drag";

export default PublishDialog;
